/*
<summary>
  This component to handle viewing, adding, and deleting payment mode.
  Developer: Sahil Bharti | Date: 14-April-2025
</summary>
<returns>JSX</returns>
*/
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useStore } from "../../../contexts/store-provider";
import { getStoreIdFromLocalStorage } from "../../../helpers/localstorage-helper";
import { formatMessage } from "../../../translations/format-message";
import DeletePopup from "../../../shared-components/popup/delete/delete-popup";
import PaymentInStoreListView from "./components/payment-in-store-list-view";
import DeletePopupHOC from "../../../shared-components/popup/delete/delete-popup-hoc";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";

interface IProps {
  id: number;
  modalClosed: () => void;
  showDeletePopup: boolean;
  deletePopupToggleHandler: () => void;
}

const PaymentInStore = (props: IProps) => {
  const { masterManagementStore } = useStore();
  const {
    addUpdatePaymentInStoreState,
    GetStorePaymentByStoreId,
    AddStorePayment,
    DeleteStorePayment,
    deletePaymentInStoreState,
    getByIdPaymentInStoreState,
    resetdDeletePaymentInStoregState,
    resetAddUpdatePaymentInStoreState,
  } = masterManagementStore;

  const { id, showDeletePopup, deletePopupToggleHandler } = props;
  const [selectedId, setSelectedId] = useState<number>(-1);
  const [editableId, setEditableId] = useState<number>(-1);

  const storeId = getStoreIdFromLocalStorage();

  const callGetMotivationList = useCallback(() => {
    GetStorePaymentByStoreId(storeId);
  }, [GetStorePaymentByStoreId]);

  const deleteHandler = () => {
    DeleteStorePayment(selectedId);
    deletePopupToggleHandler();
    setEditableId(-1);
    resetAddUpdatePaymentInStoreState();
  };

  const editHandler = (id: number) => {
    setEditableId(id);

    AddStorePayment({
      PaymentModeId: id,
      StoreId: storeId,
    });
  };

  useEffect(() => {
    callGetMotivationList();
  }, [callGetMotivationList]);

  useEffect(() => {
    if (addUpdatePaymentInStoreState.success) {
      toast.success(formatMessage("payment_setting_add_success"));
      callGetMotivationList();
      resetAddUpdatePaymentInStoreState();
      setEditableId(-1);
    }
  }, [
    addUpdatePaymentInStoreState.success,
    callGetMotivationList,
    resetAddUpdatePaymentInStoreState,
    editableId,
  ]);

  useEffect(() => {
    if (deletePaymentInStoreState.success) {
      resetdDeletePaymentInStoregState();
      toast.success(formatMessage("payment_remove_success"));
      callGetMotivationList();
    }
  }, [
    deletePaymentInStoreState.success,
    callGetMotivationList,
    resetdDeletePaymentInStoregState,
  ]);

  return (
    <React.Fragment>
      {(addUpdatePaymentInStoreState.inProgress || deletePaymentInStoreState.inProgress || getByIdPaymentInStoreState.inProgress) && (
        <FormLoader
          loading={addUpdatePaymentInStoreState.inProgress || deletePaymentInStoreState.inProgress || getByIdPaymentInStoreState.inProgress}
        />
      )}

      {showDeletePopup && (
        <DeletePopup
          title="remove_payment"
          modalSubmit={deleteHandler}
          modalClosed={deletePopupToggleHandler}
          isLoading={deletePaymentInStoreState.inProgress}
          message="remove_payment_confirm"
        />
      )}

      <PaymentInStoreListView
        id={id}
        showDeletePopup={showDeletePopup}
        deleteToggleHandler={deletePopupToggleHandler}
        setSelectedId={setSelectedId}
        selectedId={selectedId}
        editHandler={editHandler}
      />
    </React.Fragment>
  );
};

export default DeletePopupHOC(observer(PaymentInStore));
